import { captureException } from '@canvas/sentry-core';

import { getRawImageData } from './get-raw-image-data';

export interface ImageMetadata {
  /**
   * `true` if the image contains transparent pixels, `false` otherwise.
   */
  isTransparent: boolean;
  /**
   * A SHA-256 hash of the image data.
   */
  hash: string;
  /**
   * The native size of the image in pixels.
   */
  nativeSize: { x: number; y: number };
}

/**
 * Returns metadata about an image, including whether it contains transparent pixels and
 * a hash of the image data.
 *
 * If there are any issues in determining image metadata, such as `getContext('2d')` not
 * being able to create a context, `null` will be returned.
 *
 * Feel free to reconsider and refactor the above decision if you are using
 * `getImageMetadata()` in a new code location.
 *
 * @param image HTMLImageElement
 * @returns The image metadata, or `null` if the image metadata could not be determined.
 */
export async function getImageMetadata(
  image: HTMLImageElement | HTMLCanvasElement
): Promise<ImageMetadata | null> {
  try {
    const data = getRawImageData(image);

    // Check if the image contains transparent pixels
    let isTransparent = false;
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] < 255) {
        isTransparent = true;
        break;
      }
    }

    // Calculate the SHA-256 hash of the image data
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);

    // Convert the hash buffer to a hex string
    const hash = [...new Uint8Array(hashBuffer)]
      .map((x) => x.toString(16).padStart(2, '0'))
      .join('');

    return {
      isTransparent,
      hash,
      nativeSize:
        'naturalWidth' in image && 'naturalHeight' in image
          ? { x: image.naturalWidth, y: image.naturalHeight }
          : { x: image.width, y: image.height },
    };
  } catch (e) {
    console.error('Failed to get image metadata', e);
    captureException(e, {
      tags: {
        captureLocation: 'getImageMetadata',
      },
    });

    return null;
  }
}
